import axios from "axios";
import Vue from "vue";

// initial state
const state =
{
	rootNodeId: null,
	homeNodeId: null,	// Home node cache (This is absolute for this UI, which belongs to a single node)
	nodeLookup: { },	// Node definition cache lookup [nodeId] => [nodeDefinition]
}

// getters
const getters = {
	node : ( state ) => ( id ) =>
	{
		return state.nodeLookup[id] || null;
	},
	homeNode : ( state ) =>
	{
		if( !state.homeNodeId )
			return null;
		return state.nodeLookup[state.homeNodeId] || null;
	},
	rootNode : ( state ) =>
	{
		if( !state.rootNodeId )
			return null;
		return state.nodeLookup[state.rootNodeId] || null;
	},
	parent : ( state ) => ( id ) =>
	{
		let node = state.nodeLookup[id];
		if( node )
			return node.parent;	// NOTE: This can be undefined (parent unknown), or null (no parent; root ecosystem node)
	}
}

// actions
const actions =
{
	/**
	 * Query the home node to resolve the node with the given ID.
	 */
	async findNode( { state, getters, commit, dispatch }, { fromNodeId = null, id, nocache = false } )
	{
		let node = null;
		if( !nocache )
		{
			node = getters.node( id );
			if( node )
				return node;
		}

		if( !state.homeNodeId )
			await dispatch( 'getHomeNode', { } );

		fromNodeId = fromNodeId || state.homeNodeId;

		let fromNode = getters.node( fromNodeId );
		if( !fromNode || nocache )
		{
			fromNode = await dispatch( 'findNode', { id : fromNodeId } );
			if( !fromNode )
				throw new Error( "The fromNodeId provided ("+ fromNodeId +") does not resolve to any accessible node, so requested node cannot be searched for!" );
		}

		let proxyPrefix = "";
		if( fromNodeId != state.homeNodeId )
			proxyPrefix = "/proxy/"+ fromNodeId;

		try {
			let result = await axios( { method: "GET", url: process.env.APIHOST + proxyPrefix +"/api/v1/node/find/"+ id } );
			node = result.data || null;
			if( node )
				commit( 'cacheNode', node );
			return node;
		} catch( err ) {
			if( err.response && err.response.status === 404 )
			{
				commit( 'clearCache', id );
				return null;
			}
			throw err;
		}
	},
	/**
	 * Get the home node definition.
	 */
	async getHomeNode( { state, getters, commit, dispatch }, { nocache = false } )
	{
		let node = null;
		if( !nocache )
		{
			node = getters.homeNode;
			if( node ) return node;
		}
		try {
			let result = await axios( { method: "GET", url: process.env.APIHOST + "/api/v1/node" } );
			node = result.data || null;
			commit( 'setHome', node );
			return node;
		} catch( err ) {
			throw err;
		}
	},
	/**
	 * NODE ADMIN ONLY: Set the current node's new parent.
	 */
	async setNewHomeNodeParent( { state, getters, commit, dispatch }, { parentUrl, signupKey, adminKey } )
	{
		console.error( "TODO!" );
	},
	/**
	 * NODE ADMIN ONLY: Detatch current node from current parent.
	 */
	async detatchHomeNodeFromParent( { state, getters, commit, dispatch }, { adminKey } )
	{
		console.error( "TODO!" );
	},
	/**
	 * Get the parent node of the given node (or of the home node, by default)
	 * If the node is in the lookup (and nocache != true), and it has a parent field (including null), return null or that resolved node.
	 * Otherwise (or if nocache flag), 
	 */
	async getParentNode( { state, getters, commit, dispatch }, { fromNodeId = null, nocache = false } )
	{
		if( !state.homeNodeId )
			await dispatch( 'getHomeNode', { } );

		fromNodeId = fromNodeId || state.homeNodeId;
		
		let fromNode = getters.node( fromNodeId );
		if( !fromNode || nocache )
		{
			fromNode = await dispatch( 'findNode', { id : fromNodeId, nocache } );
			if( !fromNode )
				throw new Error( "The fromNodeId provided ("+ fromNodeId +") does not resolve to any accessible node, so parent cannot be retrieved!" );
		}

		let node = null;
		if( !nocache )
		{
			if( fromNode.parent )
				node = getters.node( fromNode.parent );	// Home node better exist at this point...
			if( node )
				return node;
		}

		let proxyPrefix = "";
		if( fromNodeId != state.homeNodeId )
			proxyPrefix = "/proxy/"+ fromNodeId;

		try {
			// TODO: This will depend on what "fromNode" we are operating on..
			let result = await axios( { method: "GET", url: process.env.APIHOST + proxyPrefix +"/api/v1/node/parent" } );
			node = result.data || null;
			if( node )
			{
				commit( 'cacheNode', node );
				fromNode.parent = node.$id;
			}
			else
				fromNode.parent = null;	// We actually know there is no parent.
			return node;
		} catch( err ) {
			if( err.response && err.response.status === 404 )
				return null;
			throw err;
		}
	},
	async getNodePath( { state, getters, commit, dispatch }, { fromNodeId = null, nocache = false } )
	{
		if( !state.homeNodeId )
			await dispatch( 'getHomeNode', { } );

		fromNodeId = fromNodeId || state.homeNodeId;
		
		let nodePath = [];
		let node = await dispatch( "findNode", { id : fromNodeId, nocache } );
		if( !node )
			throw new Error( "Node ["+ fromNodeId +"] not found!" );
		
		do {
			nodePath.unshift( node.$id );
			node = await dispatch( "getParentNode", { fromNodeId : node.$id, nocache } );
		} while( node );

		commit( "setRootId", nodePath[0].$id );
		return nodePath;
	},
	async listChildNodes( { state, getters, commit, dispatch }, { fromNodeId = null } )
	{
		if( !state.homeNodeId )
			await dispatch( 'getHomeNode', { } );

		fromNodeId = fromNodeId || state.homeNodeId;
		
		let fromNode = getters.node( fromNodeId );
		if( !fromNode )
		{
			let fromNode = await dispatch( 'findNode', { id : fromNodeId } );
			if( !fromNode )
				throw new Error( "The [fromNodeId] provided does not resolve to any accessible node, so child nodes cannot be retrieved!" );
		}

		let nodes = [ ];

		let proxyPrefix = "";
		if( fromNodeId != state.homeNodeId )
			proxyPrefix = "/proxy/"+ fromNodeId;

		try {
			// TODO: This will depend on what "fromNode" we are operating on..
			let result = await axios( { method: "GET", url: process.env.APIHOST + proxyPrefix +"/api/v1/node/children" } );
			nodes = result.data || [];
			for( let i=0; i < nodes.length; i++ )
			{
				let child = nodes[i];
				if( !child.parent )
					child.parent = fromNode.$id;
				commit( 'cacheNode', child );
			}
			return nodes;
		} catch( err ) {
			throw err;
		}
	},
	/**
	 * List all the subnotes of the given node (or of the home node, by default)
	 */
	async listSubNodes( { state, getters, commit, dispatch }, { fromNodeId = null } )
	{
		if( !state.homeNodeId )
			await dispatch( 'getHomeNode', { } );

		fromNodeId = fromNodeId || state.homeNodeId;
		
		let fromNode = getters.node( fromNodeId );
		if( !fromNode )
		{
			let fromNode = await dispatch( 'findNode', { id : fromNodeId } );
			if( !fromNode )
				throw new Error( "The [fromNodeId] provided does not resolve to any accessible node, so child nodes cannot be retrieved!" );
		}

		let nodes = [ ];

		let proxyPrefix = "";
		if( fromNodeId != state.homeNodeId )
			proxyPrefix = "/proxy/"+ fromNodeId;

		try {
			// TODO: This will depend on what "fromNode" we are operating on..
			let result = await axios( { method: "GET", url: process.env.APIHOST + proxyPrefix +"/api/v1/node/subnodes" } );
			nodes = result.data || [];
			for( let i=0; i < nodes.length; i++ )
				commit( 'cacheNode', nodes[i] );
			return nodes;
		} catch( err ) {
			throw err;
		}
	},
}

// mutations
const mutations =
{
	cacheNode( state, nodeDef )
	{
		Vue.set( state.nodeLookup, nodeDef.$id, nodeDef );
	},
	clearCache( state, nodeId )
	{
		Vue.delete( state.nodeLookup, nodeId );
	},
	setHome( state, nodeDef )
	{
		if( !nodeDef || !nodeDef.$id )
			throw new Error( "Must provide home node definition with an ID!" );
		Vue.set( state.nodeLookup, nodeDef.$id, nodeDef );	// Cache the definition
		state.homeNodeId = nodeDef.$id;
	},
	setRootId( state, nodeId )
	{
		state.rootNodeId = nodeId;
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/nodes.js